import type { ArdoqId } from '@ardoq/api-types';
import type { SelectOption } from '@ardoq/select';
import { useEffect, useRef, useState } from 'react';
import { TextInput } from '@ardoq/forms';
import { IconName } from '@ardoq/icons';
import { ModalSize, ModalTemplate } from '@ardoq/modal';
import {
  Table,
  type FullWidthRow,
  type Row,
  type SortDescriptor,
} from '@ardoq/table';
import styled from 'styled-components';
import { DoqLayout, DoqType, DoqWithSpeechBubble } from '@ardoq/doq';
import { Tab, TabList } from '@ardoq/tabs';
import { APISurveyResponseLog, UpdateType } from '@ardoq/api-types';
import {
  getColumns,
  isCreatedResponse,
  isUpdatedResponse,
  toResponseRowData,
  type ResponseLogRowData,
} from './responseLogColumnConfig';
import { type Locale } from '@ardoq/locale';
import { dispatchAction, connect } from '@ardoq/rxbeach';
import { ResponseLogStreamShape } from './types';
import { ChangeRowTable } from './ChangeRowTable';
import { responseLog$ } from './responseLog$';
import { fetchResponseLogData } from './actions';
import { PAGE_SIZE } from './consts';
import { Pagination } from '@ardoq/pagination';
import { dateRangeOperations, isDateRangeFieldType } from '@ardoq/date-range';
import { trackEvent } from 'tracking/tracking';
import { SurveyAdminLoader } from '../../SurveyAdminLoader';
import { InfoNotification } from '@ardoq/status-ui';
import { FlexBox } from '@ardoq/layout';
import { t, type I18nLocale } from '@ardoq/i18n';
import { combineLatest, map } from 'rxjs';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { userOptions$ } from 'scopeData/editors/user/userOptions$';

const Container = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const getEmptyMessage = (
  updateType: UpdateType,
  searchPhrase: string,
  i18nLocale: I18nLocale
): string => {
  if (searchPhrase) {
    switch (updateType) {
      case UpdateType.UPDATED:
        return t(
          i18nLocale,
          'surveyAdmin:responseLog.noResults.updates.search',
          { searchPhrase },
          'There are no update responses containing the term "{{searchPhrase}}"'
        );
      case UpdateType.CREATED:
        return t(
          i18nLocale,
          'surveyAdmin:responseLog.noResults.creations.search',
          { searchPhrase },
          'There are no creation responses containing the term "{{searchPhrase}}"'
        );
      case UpdateType.DELETED:
        return t(
          i18nLocale,
          'surveyAdmin:responseLog.noResults.deletions.search',
          { searchPhrase },
          'There are no deletion responses containing the term "{{searchPhrase}}"'
        );
    }
  }
  switch (updateType) {
    case UpdateType.UPDATED:
      return t(
        i18nLocale,
        'surveyAdmin:responseLog.noResults.updates.empty',
        'There are no update responses logged for this survey yet, please check back again later'
      );
    case UpdateType.CREATED:
      return t(
        i18nLocale,
        'surveyAdmin:responseLog.noResults.creations.empty',
        'There are no creation responses logged for this survey yet, please check back again later'
      );
    case UpdateType.DELETED:
      return t(
        i18nLocale,
        'surveyAdmin:responseLog.noResults.deletions.empty',
        'There are no deletion responses logged for this survey yet, please check back again later'
      );
  }
};

const EmptyContent = ({
  searchPhrase,
  updateType,
  responseLogErrorMessage,
  i18nLocale = {},
}: {
  searchPhrase: string;
  updateType: UpdateType;
  responseLogErrorMessage: string | null;
  i18nLocale?: I18nLocale;
}) => (
  <Container>
    <DoqWithSpeechBubble
      doqType={DoqType.MISSING_ORG}
      layout={DoqLayout.VERTICAL}
      title={
        responseLogErrorMessage
          ? t(
              i18nLocale,
              'surveyAdmin:responseLog.somethingWentWrong',
              'Something went wrong'
            )
          : t(
              i18nLocale,
              'surveyAdmin:responseLog.noResults',
              'No results found'
            )
      }
      message={
        responseLogErrorMessage ??
        getEmptyMessage(updateType, searchPhrase, i18nLocale)
      }
    />
  </Container>
);

const StyledTextInput = styled(TextInput)`
  width: 180px;
`;

type ExpandedFullWidthData = {
  userOptions: SelectOption<ArdoqId>[];
  response: APISurveyResponseLog;
  i18nLocale: I18nLocale;
};

const normalizeResponseName = (
  response: APISurveyResponseLog
): APISurveyResponseLog => {
  if (!isUpdatedResponse(response)) return response;
  return {
    ...response,
    name: isDateRangeFieldType(response.type)
      ? dateRangeOperations.dashFormatDateRangeFieldLabel(response.name)
      : response.name,
  };
};

const containsARealChange = (response: APISurveyResponseLog) => {
  if (!isUpdatedResponse(response)) return true;
  // Filter out initial name-setting rows (component created with a name via survey).
  // The backend excludes identical previousVal/currentVal rows already; this case
  // is not yet handled server-side and should be moved there in a follow-up.
  if (response.name === 'name' && response.previousVal === null) return false;
  return true;
};

const getRowId = (response: APISurveyResponseLog): string => {
  if (isUpdatedResponse(response))
    return `updated-${response.component.name}-${response.name}-${response.lastUpdated}`;
  if (isCreatedResponse(response))
    return `created-${response.name}-${response.created}`;
  return `deleted-${response.name}-${response.deleted}`;
};

const renderFullWidthRow = ({
  response,
  userOptions,
  i18nLocale,
}: ExpandedFullWidthData) =>
  isUpdatedResponse(response) ? (
    <ChangeRowTable
      surveyResponse={response}
      userOptions={userOptions}
      i18nLocale={i18nLocale}
    />
  ) : null;

type ResponseLogModalProps = {
  surveyName: string;
  initialUpdateType: UpdateType;
  closeResponseLogModal: VoidFunction;
  onComponentClick: VoidFunction;
  i18nLocale?: I18nLocale;
  locale: Locale;
  userOptions: SelectOption<ArdoqId>[];
};

const ResponseLogModalBase = ({
  surveyName,
  initialUpdateType,
  responseLogSurveyId,
  responseLogData,
  responseLogTotal,
  responseLogIsLoading,
  responseLogErrorMessage,
  closeResponseLogModal,
  onComponentClick,
  i18nLocale = {},
  locale,
  userOptions,
}: ResponseLogModalProps & ResponseLogStreamShape) => {
  const [searchPhrase, setSearchPhrase] = useState('');
  const [currentTabId, setCurrentTabId] =
    useState<UpdateType>(initialUpdateType);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor | null>(
    null
  );
  const [page, setPage] = useState(0);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const isInitialMount = useRef(true);
  const latestSortDescriptorRef = useRef(sortDescriptor);
  useEffect(() => {
    latestSortDescriptorRef.current = sortDescriptor;
  });

  const fetchPage = (
    newPage: number,
    opts?: { q?: string; sortField?: string; sortOrder?: string }
  ) => {
    if (!responseLogSurveyId) return;
    dispatchAction(
      fetchResponseLogData({
        surveyId: responseLogSurveyId,
        updateType: currentTabId,
        from: newPage * PAGE_SIZE,
        size: PAGE_SIZE,
        q: searchPhrase || undefined,
        sortField: sortDescriptor?.column,
        sortOrder: sortDescriptor?.direction,
        ...opts,
      })
    );
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setExpandedIds([]);
    fetchPage(newPage);
  };

  const handleTabChange = (newTabId: UpdateType) => {
    if (currentTabId === newTabId) return;
    setCurrentTabId(newTabId);
    setExpandedIds([]);
    setSortDescriptor(null);
    setPage(0);
    trackEvent(`Survey response log: view ${newTabId}`);
    if (!responseLogSurveyId) return;
    dispatchAction(
      fetchResponseLogData({
        surveyId: responseLogSurveyId,
        updateType: newTabId,
        from: 0,
        size: PAGE_SIZE,
        q: searchPhrase || undefined,
      })
    );
  };

  const handleSortChange = (descriptor: SortDescriptor | null) => {
    setSortDescriptor(descriptor);
    setPage(0);
    fetchPage(0, {
      sortField: descriptor?.column,
      sortOrder: descriptor?.direction,
    });
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(0);
      fetchPage(0, {
        q: searchPhrase || undefined,
        sortField: latestSortDescriptorRef.current?.column,
        sortOrder: latestSortDescriptorRef.current?.direction,
      });
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPhrase]);

  const toggleExpansion = (rowId: string) => {
    setExpandedIds(prev =>
      prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]
    );
  };

  const preppedResponses = (responseLogData ?? [])
    .filter(containsARealChange)
    .map(normalizeResponseName);

  const tableRows: (
    | Row<ResponseLogRowData>
    | FullWidthRow<ExpandedFullWidthData>
  )[] = preppedResponses.flatMap(response => {
    const rowId = getRowId(response);
    const isUpdated = isUpdatedResponse(response);
    const isExpanded = expandedIds.includes(rowId);
    const row: Row<ResponseLogRowData> = {
      id: rowId,
      data: toResponseRowData({
        response,
        isExpandable: isUpdated,
        isExpanded,
        onToggle: () => toggleExpansion(rowId),
        onComponentClick,
        locale,
      }),
    };
    if (isUpdated && isExpanded) {
      const expandedRow: FullWidthRow<ExpandedFullWidthData> = {
        id: `${rowId}-expanded`,
        fullWidth: true,
        data: { response, userOptions, i18nLocale },
      };
      return [row, expandedRow];
    }
    return [row];
  });

  const headerLabel = t(
    i18nLocale,
    'surveyAdmin:responseLog.modalTitle',
    { surveyName },
    'Response log for {{surveyName}}'
  );

  return (
    <ModalTemplate
      modalSize={ModalSize.M}
      headerText={headerLabel}
      onCloseButtonClick={closeResponseLogModal}
      fixedHeight
    >
      <TabList
        onTabChange={tabId => handleTabChange(tabId as UpdateType)}
        activeTabId={currentTabId}
        moreButtonLabel={t(i18nLocale, 'common:labels.more', 'More')}
      >
        <Tab
          tabId={UpdateType.UPDATED}
          label={t(i18nLocale, 'surveyAdmin:responseLog.updatesTab', 'Updates')}
        />
        <Tab
          tabId={UpdateType.CREATED}
          label={t(i18nLocale, 'surveyAdmin:responseLog.newTab', 'New')}
        />
        <Tab
          tabId={UpdateType.DELETED}
          label={t(i18nLocale, 'surveyAdmin:responseLog.deletedTab', 'Deleted')}
        />
      </TabList>
      <FlexBox
        paddingX="medium"
        flex={1}
        flexDirection="column"
        minHeight="0"
        gap="medium"
        paddingBottom="small"
      >
        <InfoNotification>
          {t(
            i18nLocale,
            'surveyAdmin:responseLog.infoNotice',
            'This table only shows changes to components within the survey, and not references or referenced components'
          )}
        </InfoNotification>
        <StyledTextInput
          leftIconName={IconName.SEARCH}
          onValueChange={setSearchPhrase}
          placeholder={t(
            i18nLocale,
            'surveyAdmin:responseLog.searchPlaceholder',
            'Search...'
          )}
          value={searchPhrase}
          onFocus={() =>
            trackEvent(`Survey response log: used search to filter`)
          }
        />
        {responseLogIsLoading ? (
          <SurveyAdminLoader i18nLocale={i18nLocale} />
        ) : (responseLogTotal ?? 0) === 0 ? (
          <EmptyContent
            searchPhrase={searchPhrase}
            updateType={currentTabId}
            responseLogErrorMessage={responseLogErrorMessage}
            i18nLocale={i18nLocale}
          />
        ) : (
          <FlexBox flex={1} flexDirection="column" minHeight="0" gap="small">
            <FlexBox flex={1} flexDirection="column" minHeight="0">
              <Table<ResponseLogRowData, ExpandedFullWidthData>
                aria-label={headerLabel}
                rows={tableRows}
                columns={getColumns(currentTabId, i18nLocale)}
                sortable={{
                  descriptor: sortDescriptor,
                  onChange: handleSortChange,
                  externallySorted: true,
                }}
                fullWidthRowRenderer={renderFullWidthRow}
                height="100%"
              />
            </FlexBox>
            <FlexBox justify="center">
              <Pagination
                currentPageNumber={page + 1}
                perPage={PAGE_SIZE}
                totalResults={responseLogTotal ?? 0}
                onPageSelect={newPage => handlePageChange(newPage - 1)}
              />
            </FlexBox>
          </FlexBox>
        )}
      </FlexBox>
    </ModalTemplate>
  );
};

export const ResponseLogModal = connect(
  ResponseLogModalBase,
  combineLatest([responseLog$, i18nLocale$, locale$, userOptions$]).pipe(
    map(([responseLogState, i18nLocale, locale, { options }]) => ({
      ...responseLogState,
      i18nLocale,
      locale,
      userOptions: options,
    }))
  )
);
